import { isRunningWorkout, formatRunningPace, type RunningWorkout } from '../../utils/runningWorkoutMetrics';
import { workoutStressLabel } from '../../utils/workoutStressLabel';
import { secToHMS } from '../../utils/secToHMS';

export type ComparisonRowKey = 'time' | 'distance' | 'tss' | 'if' | 'calories';
export type StatsRowKey = 'heart_rate' | 'power' | 'cadence' | 'speed' | 'respiration_rate';

export interface HeroMetricItem {
  value: string;
  label: string;
  tone: 'green' | 'gold' | 'pink';
}

interface TrainingLoadLabelData {
  workout_type?: { id?: number; name?: string; english_name?: string } | null;
  training_load?: { method?: string | null; details?: Record<string, unknown> | null } | null;
}

export function trainingLoadLabel(data: TrainingLoadLabelData): 'TSS' | 'rTSS' | 'hrTSS' {
  const sport = `${data.workout_type?.english_name ?? ''} ${data.workout_type?.name ?? ''}`.toLowerCase();
  const isRunning = data.workout_type?.id === 1
    || /(?:^|[\s_-]|road|trail|track)run(?:ning)?/.test(sport);

  if (isRunning || data.training_load?.method === 'PACE') return 'rTSS';
  if (data.training_load?.method === 'HR' && data.training_load.details?.methodology_version === 'cycling_hrtss_friel_v1') return 'hrTSS';
  return 'TSS';
}

/** Subjective feedback row under objective hero metrics (feeling + RPE). */
export interface HeroSubjectiveMetricItem {
  key: 'feeling' | 'rpe';
  value: string;
  label: string;
  isEmoji?: boolean;
}

export interface ComparisonRowItem {
  rowKey: ComparisonRowKey;
  label?: string;
  planned: string | number | null;
  done: string | number | null;
  delta?: string | null;
  deltaTone?: 'good' | 'over';
  doneTone?: 'red' | 'green' | null;
}

function comparisonDoneTone(
  planned: number | string | null | undefined,
  done: number | string | null | undefined,
): ComparisonRowItem['doneTone'] {
  if (planned == null || done == null) return null;
  const plannedValue = Number(planned);
  const doneValue = Number(done);
  if (!Number.isFinite(plannedValue) || !Number.isFinite(doneValue)) return null;
  return doneValue >= plannedValue ? 'green' : 'red';
}

export interface StatsRowItem {
  rowKey: StatsRowKey;
  /** Unit shown under the caption (bpm, W, …). */
  label: string;
  /** @deprecated Prefer i18n via `rowKey` in WsStatsTable. */
  caption?: string;
  min: string | null;
  avg: string | null;
  max: string | null;
}

/** Hero metrics — distance, TSS (actual or planned), IF (actual or planned). */
export function buildHeroMetrics(data: {
  distance?: number | null;
  distance_planned?: number | string | null;
  if_actual?: number | string | null;
  if_planned?: number | string | null;
  workout_type?: TrainingLoadLabelData['workout_type'];
  training_load?: TrainingLoadLabelData['training_load'];
} & RunningWorkout, displayTss: number | null): HeroMetricItem[] {
  const items: HeroMetricItem[] = [];
  const distance = data?.distance ?? data?.distance_planned;
  if (distance != null) {
    items.push({ value: (Number(distance) / 1000).toFixed(1), label: 'km', tone: 'green' });
  }
  if (displayTss != null) {
    items.push({ value: String(Math.round(displayTss)), label: trainingLoadLabel(data), tone: 'gold' });
  }
  const ifVal = data?.if_actual ?? data?.if_planned;
  if (ifVal != null) {
    items.push({ value: Number(ifVal).toFixed(2), label: 'IF', tone: 'pink' });
  }
  if (isRunningWorkout(data) && data.running_metrics?.avg_pace_seconds_per_km) items.push({ value: formatRunningPace(data.running_metrics.avg_pace_seconds_per_km), label: 'min/km', tone: 'green' });
  return items;
}

/** Plan vs actual rows — same logic as Ai-Tempo shared.ts. */
export function buildComparisonRows(data: {
  total_time?: number | null;
  total_time_planned?: number | string | null;
  distance?: number | null;
  distance_planned?: number | string | null;
  tss_actual?: number | string | null;
  tss_planned?: number | string | null;
  if_actual?: number | string | null;
  if_planned?: number | string | null;
  calories?: number | string | null;
  calories_planned?: number | string | null;
} & RunningWorkout): ComparisonRowItem[] {
  const rows: ComparisonRowItem[] = [];

  if (data?.total_time || data?.total_time_planned) {
    const planned = data?.total_time_planned != null ? Number(data.total_time_planned) : null;
    const done = data?.total_time ?? null;
    let delta: string | null = null;
    let deltaTone: ComparisonRowItem['deltaTone'] = 'good';
    if (planned && done) {
      const diffMin = Math.round((done - planned) / 60);
      if (diffMin !== 0) {
        delta = `${diffMin > 0 ? '+' : ''}${diffMin}m`;
        deltaTone = diffMin > 0 ? 'over' : 'good';
      }
    }
    rows.push({
      rowKey: 'time',
      planned: planned ? secToHMS(planned) : null,
      done: done ? secToHMS(done) : null,
      delta,
      deltaTone,
      doneTone: comparisonDoneTone(planned, done),
    });
  }

  if (data?.distance != null || data?.distance_planned != null) {
    rows.push({
      rowKey: 'distance',
      planned: data?.distance_planned != null ? `${Math.round(Number(data.distance_planned) / 1000)} km` : null,
      done: data?.distance != null ? `${Math.round(data.distance / 1000)} km` : null,
      doneTone: comparisonDoneTone(data?.distance_planned, data?.distance),
    });
  }

  if (data?.tss_actual != null || data?.tss_planned != null) {
    const planned = data?.tss_planned;
    const done = data?.tss_actual;
    let delta: string | null = null;
    let deltaTone: ComparisonRowItem['deltaTone'] = 'good';
    if (planned != null && done != null) {
      const diff = Math.round(Number(done) - Number(planned));
      if (diff !== 0) {
        delta = `${diff > 0 ? '+' : '−'}${Math.abs(diff)}`;
        deltaTone = Math.abs(diff) > Number(planned) * 0.15 ? 'over' : 'good';
      }
    }
    rows.push({
      rowKey: 'tss',
      label: workoutStressLabel(data),
      planned: planned != null ? Math.round(Number(planned)) : null,
      done: done != null ? Math.round(Number(done)) : null,
      delta,
      deltaTone,
      doneTone: comparisonDoneTone(planned, done),
    });
  }

  if (data?.if_actual != null || data?.if_planned != null) {
    const planned = data?.if_planned;
    const done = data?.if_actual;
    let delta: string | null = null;
    let deltaTone: ComparisonRowItem['deltaTone'] = 'good';
    if (planned != null && done != null) {
      const diff = Number(done) - Number(planned);
      if (Math.abs(diff) >= 0.01) {
        delta = `${diff >= 0 ? '+' : '−'}${Math.abs(diff).toFixed(2)}`;
        deltaTone = Math.abs(diff) > 0.15 ? 'over' : 'good';
      }
    }
    rows.push({
      rowKey: 'if',
      planned: planned != null ? Number(planned).toFixed(2) : null,
      done: done != null ? Number(done).toFixed(2) : null,
      delta,
      deltaTone,
      doneTone: comparisonDoneTone(planned, done),
    });
  }

  if (data?.calories != null || data?.calories_planned != null) {
    rows.push({
      rowKey: 'calories',
      planned: data?.calories_planned != null ? Math.round(parseInt(String(data.calories_planned), 10)) : null,
      done: data?.calories != null ? Math.round(parseInt(String(data.calories), 10)) : null,
      doneTone: comparisonDoneTone(data?.calories_planned, data?.calories),
    });
  }

  return rows;
}

/** Min / avg / max sensor stats. */
export function buildStatsRows(mma: {
  heart_rate?: { min?: number; avg?: number; max?: number };
  power?: { min?: number; avg?: number; max?: number };
  cadence?: { min?: number; avg?: number; max?: number };
  speed?: { min?: number; avg?: number; max?: number };
  respiration_rate?: { min?: number | null; avg?: number | null; max?: number | null };
} | null | undefined): StatsRowItem[] {
  if (!mma) return [];
  const fmt = (v: unknown, frac = 0) => {
    const n = Number(v);
    return v != null && Number.isFinite(n) ? n.toFixed(frac) : null;
  };
  const rows: StatsRowItem[] = [];
  if (mma.heart_rate?.min != null || mma.heart_rate?.avg != null || mma.heart_rate?.max != null) {
    rows.push({
      rowKey: 'heart_rate',
      label: 'bpm',
      min: fmt(mma.heart_rate?.min),
      avg: fmt(mma.heart_rate?.avg),
      max: fmt(mma.heart_rate?.max),
    });
  }
  if (mma.power?.avg != null || mma.power?.max != null) {
    rows.push({
      rowKey: 'power',
      label: 'W',
      min: fmt(mma.power?.min),
      avg: fmt(mma.power?.avg),
      max: fmt(mma.power?.max),
    });
  }
  if (mma.cadence?.avg != null || mma.cadence?.max != null) {
    rows.push({
      rowKey: 'cadence',
      label: 'rpm',
      min: fmt(mma.cadence?.min),
      avg: fmt(mma.cadence?.avg),
      max: fmt(mma.cadence?.max),
    });
  }
  if (mma.speed?.avg != null || mma.speed?.max != null || mma.speed?.min != null) {
    rows.push({
      rowKey: 'speed',
      label: 'km/h',
      min: fmt(mma.speed?.min, 1),
      avg: fmt(mma.speed?.avg, 1),
      max: fmt(mma.speed?.max, 1),
    });
  }
  if (mma.respiration_rate && Object.values(mma.respiration_rate).some(value => value != null && Number.isFinite(Number(value)) && Number(value) > 0)) {
    rows.push({ rowKey: 'respiration_rate', label: 'breaths/min', min: fmt(mma.respiration_rate.min, 1), avg: fmt(mma.respiration_rate.avg, 1), max: fmt(mma.respiration_rate.max, 1) });
  }
  return rows;
}
